// SignIn / SignUp — email + password form with toggle.
// Lives outside AdminLayout per §14.11.

import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Loader2, CheckCircle, Mail, Sparkles } from 'lucide-react'
import { SynvyaMark } from '@synvya/portal-ui/branding'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { useAuth } from '@/state/useAuth'
import { useTeam } from '@/state/useTeam'
import { storeInviteRedirect, isInviteRedirect } from '@/lib/inviteRedirect'
import { parsePlanIntent, storePlanIntent } from '@/lib/planIntent'

type AuthMode = 'signin' | 'signup'
type AuthStep = 'idle' | 'connecting' | 'success' | 'verify-email'

interface SignInPageProps {
  onSuccess: () => void
}

export function SignInPage({ onSuccess }: SignInPageProps) {
  const { t } = useTranslation('auth')
  const { t: tBilling } = useTranslation('billing')
  const { login, register } = useAuth()
  const { loadTeams } = useTeam()
  const [params] = useSearchParams()

  // Invite flow: an invitee arrives with ?redirect=/accept-invite...&inviteEmail=...
  // The invited email is prefilled and LOCKED so the new account matches the
  // invitation — the server 403s at accept if the emails differ (#17).
  const redirectParam = params.get('redirect')
  const inviteEmailParam = params.get('inviteEmail')
  const isInviteFlow = isInviteRedirect(redirectParam)
  const lockedEmail = isInviteFlow && inviteEmailParam ? inviteEmailParam : null

  // Plan intent from the website's pricing cards (?plan=premium&interval=annual).
  // Stashed on submit so it survives the verify-email round-trip; the wizard's
  // plan step consumes it (billing-signup.md §5.1).
  const planIntent = parsePlanIntent(params)

  const [mode, setMode] = useState<AuthMode>(
    params.get('mode') === 'signup' || lockedEmail ? 'signup' : 'signin',
  )
  const [step, setStep] = useState<AuthStep>('idle')
  const [email, setEmail] = useState(lockedEmail ?? '')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)

    if (mode === 'signup' && password !== confirmPassword) {
      setError(t('passwordMismatch'))
      return
    }

    // Persist the invite target before signup so it survives the
    // email-verification round-trip and is consumed on the later sign-in.
    if (isInviteRedirect(redirectParam)) {
      storeInviteRedirect(redirectParam)
    }
    if (planIntent) {
      storePlanIntent(planIntent)
    }

    setStep('connecting')

    try {
      if (mode === 'signup') {
        await register(email, password)
        // Check if email verification is required
        if (useAuth.getState().verificationRequired) {
          setStep('verify-email')
          return
        }
      } else {
        await login(email, password)
      }
      await loadTeams()
      setStep('success')
      setTimeout(onSuccess, 600)
    } catch (err) {
      setStep('idle')
      const msg = err instanceof Error ? err.message : null
      const storeErr = useAuth.getState().error
      setError(msg ?? storeErr ?? (mode === 'signup' ? t('signUpError') : t('signInError')))
    }
  }

  function toggleMode() {
    setMode(mode === 'signin' ? 'signup' : 'signin')
    setError(null)
    setConfirmPassword('')
  }

  const title = mode === 'signin' ? t('signInTitle') : t('signUpTitle')
  const subtitle = mode === 'signin' ? t('signInSubtitle') : t('signUpSubtitle')
  const submitLabel = mode === 'signin' ? t('signInButton') : t('signUpButton')

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-6">
      {/* Synvya mark */}
      <span data-testid="signin-mark" className="mb-8 flex items-center justify-center">
        <SynvyaMark variant="green" size={48} title="Synvya" />
      </span>

      {/* Headline */}
      <h1 className="mb-2 text-center text-xl font-bold text-foreground">
        {title}
      </h1>
      <p className="mb-8 text-center text-sm text-muted-foreground">
        {subtitle}
      </p>

      {planIntent && mode === 'signup' && step === 'idle' && (
        <span data-testid="plan-intent-chip" className="-mt-4 mb-6 flex flex-col items-center gap-1">
          <Badge variant="soft" className="gap-1 normal-case tracking-normal">
            {planIntent.plan === 'premium' && <Sparkles className="h-3 w-3" />}
            {tBilling(planIntent.plan === 'premium' ? 'premiumName' : 'basicName')}
          </Badge>
          {planIntent.plan === 'basic' && (
            <span className="text-xs text-muted-foreground">{tBilling('cardNotice')}</span>
          )}
        </span>
      )}

      {/* Form / States */}
      {step === 'idle' && (
        <form onSubmit={handleSubmit} className="w-full max-w-xs space-y-4">
          <div>
            <label htmlFor="email" className="mb-1 block text-sm font-medium text-foreground">
              {t('email')}
            </label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              readOnly={Boolean(lockedEmail)}
              disabled={Boolean(lockedEmail)}
              className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-70"
              autoComplete="email"
            />
            {lockedEmail && (
              <p className="mt-1 text-xs text-muted-foreground">
                {t('inviteEmailLocked', {
                  defaultValue: 'This invitation was sent to this address.',
                })}
              </p>
            )}
          </div>
          <div>
            <label htmlFor="password" className="mb-1 block text-sm font-medium text-foreground">
              {t('password')}
            </label>
            <input
              id="password"
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
            />
          </div>
          {mode === 'signup' && (
            <div>
              <label htmlFor="confirmPassword" className="mb-1 block text-sm font-medium text-foreground">
                {t('confirmPassword')}
              </label>
              <input
                id="confirmPassword"
                type="password"
                required
                minLength={8}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                autoComplete="new-password"
              />
            </div>
          )}
          {error && (
            <p className="text-sm text-destructive" role="alert">{error}</p>
          )}
          <Button type="submit" className="w-full">
            {submitLabel}
          </Button>
          <p className="text-center text-sm text-muted-foreground">
            {mode === 'signin' ? t('noAccount') : t('hasAccount')}{' '}
            <button
              type="button"
              onClick={toggleMode}
              className="font-medium text-primary hover:underline"
            >
              {mode === 'signin' ? t('signUpLink') : t('signInLink')}
            </button>
          </p>
        </form>
      )}

      {step === 'connecting' && (
        <div className="flex flex-col items-center gap-3">
          <Loader2
            data-testid="signin-spinner"
            className="h-8 w-8 animate-spin text-primary"
          />
          <p className="text-sm text-muted-foreground">{t('connecting')}</p>
        </div>
      )}

      {step === 'verify-email' && (
        <div className="flex flex-col items-center gap-4 text-center" data-testid="verify-email">
          <Mail className="h-10 w-10 text-primary" />
          <p className="text-lg font-semibold text-foreground">{t('verifyEmailTitle')}</p>
          <div className="max-w-xs text-sm text-muted-foreground">
            <p>{t('verifyEmailLine1')}</p>
            <p className="my-1 font-semibold text-foreground">{email}</p>
            <p>{t('verifyEmailLine2')}</p>
          </div>
        </div>
      )}

      {step === 'success' && (
        <div className="flex flex-col items-center gap-3" data-testid="signin-success">
          <CheckCircle className="h-8 w-8 text-emerald-400" />
          <p className="text-sm font-medium text-emerald-400">{t('connected')}</p>
        </div>
      )}

      {/* Legal links */}
      <div className="mt-8 flex gap-3 text-xs text-muted-foreground">
        <a href="/legal/privacy" className="hover:text-foreground hover:underline">Privacy Policy</a>
        <span>·</span>
        <a href="/legal/terms" className="hover:text-foreground hover:underline">Terms of Service</a>
      </div>
    </div>
  )
}
